/**
 * Datasets 목록의 표 소스.
 *
 * BE 는 목록을 **배열 하나로** 주고 페이지네이션 · 검색 · 정렬 파라미터가 없다. 그래서 조회는
 * 한 번이고 검색은 이미 가진 배열을 다시 거른다 — 글자를 칠 때마다 250ms 왕복을 쓰지 않는다
 * (규칙 §6). 숨김 축은 서버가 답을 좁히므로 **더 넓게 볼 때만** 다시 받는다.
 *
 * Owns:
 * - 조회 시점, 클라이언트 필터, 행 생성, 노출 토글 결과의 반영.
 *
 * Does not own:
 * - 축 값 · URL — `feature/datasetFilters.ts`. 셀 표현 — `datasetColumns.tsx`.
 */
import type { ReactNode } from "react";
import { DataTableSource } from "@carbon6/c6-frontend-design";
import type {
  DataTablePageReq,
  DataTablePageResp,
  DataTableSortState,
} from "@carbon6/c6-frontend-design";
import { listDatasets } from "../api/datasetApi";
import { isDatasetHidden, type Dataset } from "../domain/dataset";
import {
  EMPTY_DATASET_FILTERS,
  matchesDatasetSearch,
  type DatasetFilters,
} from "../feature/datasetFilters";
import { withProbeChange, type ProbeChange } from "../feature/probeChanges";
import { DatasetTableRow } from "./DatasetTableRow";
import { datasetInitialSort } from "./datasetColumns";
import { LIST_PAGE_SIZE, paginateRows } from "./paginateRows";

export type DatasetTableSourceOptions = {
  onOpen: (dataset: Dataset) => void;
  renderActions?: (dataset: Dataset) => ReactNode;
  resolveErrorMessage: (error: unknown) => string;
};

export class DatasetTableSource extends DataTableSource<DatasetTableRow> {
  private readonly rowOptions: {
    onOpen: (dataset: Dataset) => void;
    renderActions?: (d: Dataset) => ReactNode;
  };
  private filters: DatasetFilters = EMPTY_DATASET_FILTERS;
  private loaded: readonly Dataset[] = [];
  /** 마지막 조회가 숨김까지 받아 왔는지. `null` 이면 아직 받은 적이 없다. */
  private loadedIncludeHidden: boolean | null = null;

  constructor({ onOpen, renderActions, resolveErrorMessage }: DatasetTableSourceOptions) {
    super({ resolveErrorMessage, initialPageSize: LIST_PAGE_SIZE });
    this.rowOptions = { onOpen, renderActions };
    this.sortState = datasetInitialSort;
  }

  override get sortsOnServer(): boolean {
    return true;
  }

  override setSort(sort: DataTableSortState | null): void {
    const changed =
      this.sortState?.columnKey !== sort?.columnKey ||
      this.sortState?.direction !== sort?.direction;
    super.setSort(sort);
    if (changed && this.getSnapshot().loadState !== "idle") void this.goToPage(1);
  }

  /**
   * 축을 적용한다. 축이 그대로면 아무 일도 하지 않지만, **아직 한 번도 받지 않았으면**
   * 축이 기본값이어도 첫 조회를 돈다 — 페이지 진입이 이 경로 하나이기 때문이다.
   */
  async applyFilters(next: DatasetFilters): Promise<void> {
    const unchanged =
      next.search === this.filters.search && next.includeHidden === this.filters.includeHidden;
    this.filters = next;
    if (unchanged && this.loadedIncludeHidden !== null) {
      return;
    }
    await this.goToPage(1);
  }

  /** 사용자가 누른 새로고침 — 캐시한 배열을 버리고 서버에 다시 묻는다. */
  async reload(): Promise<void> {
    this.loadedIncludeHidden = null;
    await this.refresh();
  }

  /** 상세 화면이 알린 Probe 변화를 그 행에 반영한다. 바뀐 행이 없으면 다시 그리지 않는다. */
  async applyProbeChange(change: ProbeChange): Promise<void> {
    const next = this.loaded.map((dataset) => withProbeChange(dataset, change));
    if (next.every((dataset, at) => dataset === this.loaded[at])) {
      return;
    }
    this.loaded = next;
    await this.refresh();
  }

  /** hide/show 응답을 목록에 반영한다. 같은 조직 목록을 다시 받지 않는다. */
  async applyDataset(dataset: Dataset): Promise<void> {
    this.loaded = this.loaded.map((row) => (row.uuid === dataset.uuid ? dataset : row));
    await this.refresh();
  }

  protected override async onPage(
    req: DataTablePageReq,
  ): Promise<DataTablePageResp<DatasetTableRow>> {
    // 숨김을 켜면 서버가 더 넓은 답을 갖고 있다. 끄는 방향은 이미 가진 배열의 부분집합이다.
    if (
      this.loadedIncludeHidden === null ||
      (this.filters.includeHidden && !this.loadedIncludeHidden)
    ) {
      this.loaded = await listDatasets(this.filters.includeHidden);
      this.loadedIncludeHidden = this.filters.includeHidden;
    }

    const visible = this.loaded.filter(
      (dataset) =>
        (this.filters.includeHidden || !isDatasetHidden(dataset)) &&
        matchesDatasetSearch(dataset, this.filters.search),
    );
    const rows = visible.map((dataset) => new DatasetTableRow(dataset, this.rowOptions));

    return paginateRows(rows, req, this.sortState);
  }
}
